import { ComponentProps } from "react";
import { getEnvStoryQueueId } from "@caas/getEnvStoryQueueId";
import DTAFooter from "@dta/components/DTAFooterComponents/DTAFooter";
import E50Footer from "@e50/components/E50FooterComponents/E50Footer";
import EEFooter from "@ee/components/EEFooterComponents/EEFooter";
import Card from "@particles/card/Card";
import SBAFooter from "@sba/components/SBAFooterComponents/SBAFooter";
import SCAFooter from "@sca/components/SCAFooterComponents/SCAFooter";
import SIAFooter from "@sia/components/SIAFooterComponents/SIAFooter";
import { Element, SanitizedFeedSectionArticle } from "@typings/Cue";
import { ENVIRONMENT } from "@util/constant";
import dayjs from "dayjs";

import { AwardType } from "./types";

/**
 * Returns the storyqueue Id for the Event Awards page component.
 * @param awardType - The type of awards.
 * @returns {string} - The card props for the Top stories component.
 */
export const eventsAwardsMainPageStoryQueueId = (
  awardType: AwardType
): string => {
  switch (awardType) {
    case AwardType.SBA:
      return getEnvStoryQueueId("1295782", "1295782", "2803541", ENVIRONMENT);

    case AwardType.SCA:
      return getEnvStoryQueueId("1295790", "1295790", "2803492", ENVIRONMENT);

    case AwardType.SIA:
      return getEnvStoryQueueId("1295781", "1295781", "2803552", ENVIRONMENT);

    case AwardType.E50:
      return getEnvStoryQueueId("1295479", "1295479", "2798972", ENVIRONMENT);

    case AwardType.EE:
      return getEnvStoryQueueId("1295783", "1295783", "1210671", ENVIRONMENT);

    case AwardType.DATA:
      return getEnvStoryQueueId("1346378", "1346378", "6488134", ENVIRONMENT);
  }
};

/**
 * Returns the card props for the Event Awards page component.
 * @param articles - The articles to be displayed in the component.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getEventAwardsArticleProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles.map(({ id, title, urlPath, media, kicker }) => {
    const _defaultImage = { directoryName: "events-awards" };
    const mainMedia = media[0];
    const imgSrc = mainMedia?.landscape?.url;
    const imgWidth = mainMedia?.landscape?.width;
    const imgHeight = mainMedia?.landscape?.height;

    const props: ComponentProps<typeof Card> = {
      id,
      title,
      link: urlPath,
      thumbnail:
        imgSrc && imgWidth && imgHeight
          ? {
              src: imgSrc,
              alt: mainMedia.alt || mainMedia.caption || "",
              width: imgWidth,
              height: imgHeight,
            }
          : undefined,
      kicker: kicker?.fields?.[0]?.value,
      defaultImage: _defaultImage,
    };

    return props;
  });

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the card props for the Event Awards SIA component.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getEventAwardsHomePageArticleProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles.map(
    ({ id, title, urlPath, media, kicker, blurb }) => {
      const _defaultImage = { directoryName: "events-awards" };
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath,
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker: kicker?.fields?.[0]?.value,
        defaultImage: _defaultImage,
        blurb: blurb,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Determines if a menu link should be marked as active based on the current URL.
 *
 * - Trims trailing slashes from both `menuLink` and `pathname` for comparison.
 * - Returns true if:
 *   - The current path matches the menu link exactly, or
 *   - The current path starts with the menu link and includes `/gallery/` (to handle nested routes like galleries).
 *
 * @param menuLink - The absolute path of the menu link/component (e.g., "/events").
 * @param pathname - The current URL path from the window location (e.g., "/events/gallery/2025").
 * @returns A boolean indicating whether the link is active.
 */
export const isActiveLink = (menuLink: string, pathname: string) => {
  const currentPath = pathname.replace(/\/$/, "");
  const baseMenuLink = menuLink.replace(/\/$/, "");

  return (
    currentPath === baseMenuLink ||
    (currentPath.startsWith(baseMenuLink) && currentPath.includes("/gallery/"))
  );
};

/**
 * Returns the corresponding formatted date for Header.
 *
 * This function is typically used to format the todays date in a readable format for
 * Sustainability Impact Awards (SIA) Header.
 *
 * @returns The formatted date string in 'EEE, dd MMM yyyy' format.
 */
export const formatDateToReadable = () => {
  const date = dayjs();

  return date.format("ddd, MMM DD, YYYY");
};

/**
 * Returns the corresponding footer component for events-awards.
 *
 * This function is typically used to return the footer component for the particular award
 * Sustainability Impact Awards (SIA) Footer.
 *
 * @returns The footer component.
 */
export const EventsAwardsFooterFactory = (awardType: AwardType) => {
  switch (awardType) {
    case AwardType.SIA:
      return SIAFooter;

    case AwardType.SBA:
      return SBAFooter;

    case AwardType.SCA:
      return SCAFooter;

    case AwardType.E50:
      return E50Footer;

    case AwardType.EE:
      return EEFooter;

    case AwardType.DATA:
      return DTAFooter;
  }
};

/**
 * Returns the corresponding award type based on the provided page identifier.
 *
 * This function is typically used to retun the type of awards from
 * EventsAwards Static Pages pages like "SIA", "SBA", or "how-to-nominate".
 *
 * @param urlpath - The string identifier of the page url (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const getAwardType = (urlPath: string) => {
  switch (true) {
    case urlPath.includes("sustainability-impact-awards"):
      return AwardType.SIA;

    case urlPath.includes("singapore-business-awards"):
      return AwardType.SBA;

    case urlPath.includes("singapore-corporate-awards"):
      return AwardType.SCA;

    case urlPath.includes("enterprise-50"):
      return AwardType.E50;

    case urlPath.includes("design-ai-and-tech-awards"):
      return AwardType.DATA;

    case urlPath.includes("emerging-enterprise"):
      return AwardType.EE;

    default:
      return AwardType.SIA;
  }
};

export type Image = Element & {
  type: "image";
  relation: {
    fields: {
      "landscape-caas": {
        url: string;
        width: number;
        height: number;
      };
    };
  };
};

export const isImageElement = (element: Element): element is Image => {
  return (
    element.type === "image" &&
    element.relation?.fields?.["landscape-caas"]?.url !== undefined
  );
};

export const getAllImages = (
  articles: SanitizedFeedSectionArticle[] = []
): Image[] => {
  return (
    articles?.flatMap(
      (article) => article.elements?.filter(isImageElement) ?? []
    ) ?? []
  );
};

/**
 * Returns the meta tag details for the Event Awards components.
 * @param title - The title for Metatag Data.
 * @param description - The description for Metatag Data.
 * @param slug - The url link for Metatag Data.
 * @param gaTitle - The gaTitle for Metatag Data.

 * @returns {string} - The card props for the Top stories component.
 */
export const createMetaTag = (
  title: string,
  description: string,
  slug: string,
  gaTitle: string
) => ({
  title: `${title}, - THE BUSINESS TIMES"`,
  description: description,
  slug: slug,
  gaTitle: gaTitle,
});
